import { useState, useEffect, useCallback, useRef } from "react";
import { embedApiFetch, ApiError } from "../../embed-shared/api-client";
import { executeRecaptchaToken } from "../../embed-shared/recaptcha";
import { EmbedButton, EmbedInput, useEmbedTokens } from "../../embed-shared/primitives";
import {
  isPasskeySupported,
  isPlatformAuthenticatorAvailable,
  isConditionalMediationAvailable,
} from "../../embed-shared/passkey";
import { signInWithPasskey, signInWithPasskeyConditional, listPasskeys } from "./api/passkeys";
import { PasskeyEnrollPrompt } from "./PasskeyEnrollPrompt";
import { ceremonyMessageOf } from "./passkey-ceremony-errors";

// -- Two-step OTP sign-in prompt (TD-221) ------------------------------------
//
// Step 1 ("email"): user enters email → POST /auth/request-otp with optional
//   reCAPTCHA token. On success, advances to step 2.
// Step 2 ("code"): user enters 6-digit code → POST /auth/verify-otp. On
//   success, calls onSignIn(jwt, refreshToken?) — the refresh token
//   rides along when the backend has TD-366 session renewal wired. Resend-code
//   action re-runs step 1.
//
// reCAPTCHA is lazy-injected only on first submit (project rule #20).
// When recaptchaSiteKey is empty the flow sends recaptcha_token:"". The
// handler passes the token through to the service (no handler-level
// validation). The service verifies when s.recaptcha != nil (keyed prod
// = fail-closed) and bypasses when nil (keyless dev = §18 nil-guard).

/**
 * Operator item (r), 2026-08-16: "Marketplace Sign in ui, should tell user its
 * sign in or create free account to continue, so user don't get confused that
 * they don't have account". Was "Sign in to continue", which reads as a
 * precondition — a first-time visitor sees it and assumes they are in the
 * wrong place.
 *
 * TRUTHFULNESS (checked, not assumed — this exact claim was REMOVED as false
 * on 2026-08-05 and is only sayable again because the backend moved):
 * completing this flow really does create an account for someone who has
 * none. CustomerAuthService.RequestOTP stages a real bcrypt-hashed code in
 * `pending_signups` (migration 074) for an unknown email and sends it; the
 * verifyPendingSignup branch of VerifyOTP then promotes that row inside one
 * transaction — CreateUserInTx + provisionCustomerInTx + DeleteInTx. Before
 * that path existed, an unknown email got no code at all, so the old wording
 * would have been a lie. See the supersede note in SignInPanel.test.tsx.
 *
 * ONE constant, deliberately: this is the sole sign-in heading in the embed —
 * SignInOverlay renders no copy of its own, it wraps this panel — so a future
 * reword is a one-line change here and both surfaces move together. Both
 * co-located tests assert against this binding rather than a duplicate of its
 * current value (§55).
 */
// 2026-09-05: shortened from "Sign in or create a free account to continue"
// (9 words → 5) per the operator's "less words, simple, clean" direction.
// It is NOT shortened to a bare "Sign in", which was the first attempt and is
// wrong: operator item (r) of 2026-08-16 requires this heading to tell a
// first-time visitor they can create an account here, so they don't assume
// they need one already. The co-located intent pins (/create/, /account/,
// /sign in/) are what caught that regression — they are an INTENT pin, not a
// value pin, and they did their job.
export const SIGN_IN_HEADING = "Sign in or create an account";

// Session-scoped (module-level, NOT component state and NOT
// sessionStorage/localStorage — project rule 7: this is throwaway UX
// memory, never a substitute for real backend state): true once the
// post-signup passkey-enrollment offer (Task 11) has been resolved this
// browser session — shown-and-dismissed, shown-and-completed, or found
// unavailable/ineligible. SignInPanel remounts every time the sign-in gate
// reopens (a session-expiry re-auth, a toolbar-initiated sign-in, etc.), so
// component state alone cannot remember "already offered" across that
// remount — this flag can, and resets naturally on a full page reload, the
// same lifetime as every other in-memory concept in this embed (see
// auth-context.tsx's own sessionStorage-vs-memory framing).
let enrollPromptOfferedThisSession = false;

/**
 * TEST-ONLY: resets the module-level "already offered this session" flag.
 * Production code never calls this — there is no legitimate in-product
 * reason to forget the offer mid-session. Exists purely so tests in the
 * same file don't leak state across `it()` blocks (module state persists
 * for the life of a test file's module graph); mirrors index.tsx's
 * stopObservingMounts(), which exists for the same reason.
 */
export function __resetEnrollPromptSessionStateForTests(): void {
  enrollPromptOfferedThisSession = false;
}

export interface SignInPanelProps {
  onSignIn: (jwt: string, refreshToken?: string) => void;
  recaptchaSiteKey?: string;
  /**
   * Whether THIS MOUNT is allowed to offer passkey sign-in, resolved by the
   * caller at RUNTIME (mount `data-config` and/or a server-provided value —
   * design §5.4). Never inline this at build time: the reCAPTCHA site-key
   * incident (2026-08-25, ~14h portal-login outage — build-commands.md §57)
   * is exactly the failure mode a build-time-baked eligibility flag would
   * repeat. Absent/false is the safe default — the button simply does not
   * render and email-OTP remains the only path (rule §R2: OTP is never
   * gated on this feature).
   */
  passkeyEligible?: boolean;
  /**
   * Notified (Task 11) whenever this panel starts or stops showing the
   * post-signup passkey-enrollment offer, so the caller (SignInOverlay →
   * MarketplaceRouter) can keep the sign-in overlay mounted through that
   * extra step — see finishOtpSignIn below for why that matters. Optional:
   * a caller that never uses the enrollment prompt (e.g. a future
   * standalone embedding of this panel) can simply omit it.
   */
  onEnrollPromptActiveChange?: (active: boolean) => void;
}

type SignInStep = "email" | "code";

export function SignInPanel({ onSignIn, recaptchaSiteKey, passkeyEligible, onEnrollPromptActiveChange }: SignInPanelProps) {
  const t = useEmbedTokens();
  const [step, setStep] = useState<SignInStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [stepUp, setStepUp] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  // Task 11: true while showing the post-signup passkey-enrollment offer in
  // place of the normal form. See finishOtpSignIn/handleEnrollDone below.
  const [showEnrollPrompt, setShowEnrollPrompt] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Render the passkey action only when BOTH the mount is eligible (caller's
  // runtime prop) AND the platform advertises WebAuthn support. Either gate
  // missing => no button, OTP is the only path. isPasskeySupported() reads
  // window.PublicKeyCredential at call time (not a build-time constant), so
  // this re-evaluates per render rather than freezing a stale platform read.
  //
  // Strict `=== true`, NOT Boolean(...) (remediation finding A1): passkeyEligible
  // arrives from a mount's `data-config`, which is parsed with JSON.parse and
  // then flows into this `boolean` prop with no runtime typecheck — `any` all
  // the way down. Boolean() is truthiness: the strings "false"/"0"/"no" and any
  // non-empty object all coerce to `true`, so a malformed or misconfigured
  // signal (e.g. an operator writing data-config='{"passkeyEligible":"false"}'
  // meaning to opt OUT) would render the button anyway. The doc-comment on the
  // prop above already commits to "absent/false is the safe default" and cites
  // the 2026-08-25 reCAPTCHA build-time-key incident as the failure family this
  // must not repeat -- strict equality is what actually enforces that.
  const showPasskeySignIn = passkeyEligible === true && isPasskeySupported();

  // -- Post-signup passkey enrollment offer (Task 11) -------------------------
  //
  // Completes an OTP-verified sign-in (handleVerifyOtp / handleRecaptchaStepUp
  // both route through here rather than calling onSignIn directly). onSignIn
  // ALWAYS fires immediately and unconditionally below — never deferred: it
  // is what makes the customer's fresh JWT live for embedApiFetch (via
  // AuthProvider's ambient token provider), which PasskeyEnrollPrompt's own
  // registerPasskey() call needs to succeed. What's conditional is whether we
  // ALSO show the offer first.
  //
  // Gated on:
  //  - passkeyEligible (RULING R-M) — passkeys only work on the aldelo.com
  //    relying-party origin; offering enrollment on an ineligible mount would
  //    fail the browser's own WebAuthn origin check, not just look odd.
  //  - !enrollPromptOfferedThisSession — "not on every sign-in": a customer
  //    who re-authenticates later in the SAME session is not re-pestered.
  //  - isPlatformAuthenticatorAvailable() — checked here (not left solely to
  //    PasskeyEnrollPrompt's own internal check) so an ineligible-platform
  //    customer's sign-in completes with zero added UI/latency; when it IS
  //    available, PasskeyEnrollPrompt re-checks independently anyway (it must
  //    remain safe to mount standalone — see its own tests).
  //
  // KNOWN LIMITATION (see task-11-report.md): verify-otp's response carries
  // no signal distinguishing "this call just created the account" from "this
  // call signed an existing customer back in" — by design, the same
  // anti-enumeration reasoning that makes request-otp/verify-otp uniform
  // regardless of account existence (§70, customer_auth_service.go) applies
  // to their shape here too, and no such field exists on CustomerAuthResult.
  // This offers on the first OTP-verified sign-in EACH SESSION regardless of
  // which branch the backend took — the closest safe approximation of "after
  // account creation" available without a new backend contract, which is out
  // of this task's scope (frontend-only). It is UX-only and non-blocking
  // either way (R2), so the imprecision costs nothing but an occasional
  // redundant offer to a returning customer starting a fresh browser session.
  //
  // WHY holding SignInOverlay open matters: MarketplaceRouter's `gated` flips
  // false the instant token becomes truthy, unmounting SignInOverlay (thus
  // this panel) on the very next render — so anything this component tried
  // to show AFTER calling onSignIn would never actually paint. Reporting
  // "the offer is active" via onEnrollPromptActiveChange BEFORE calling
  // onSignIn, in the same synchronous continuation (no `await` between the
  // two), lets React 18's automatic batching apply both state updates (the
  // token flip AND pendingEnrollOffer) in ONE render — `gated` computes with
  // both already reflecting the final state, so there is no unmount flicker.
  const finishOtpSignIn = useCallback(
    async (token: string, refreshToken?: string) => {
      let offerEnroll = false;
      // Strict `=== true`, same as showPasskeySignIn above (remediation A1,
      // extended per coordinator follow-up): passkeyEligible is read TWICE
      // in this file, and a bare truthiness check here would leave a
      // residual asymmetry — a malformed truthy value would hide the
      // sign-in BUTTON while still firing the enrollment PROMPT.
      if (passkeyEligible === true && !enrollPromptOfferedThisSession) {
        enrollPromptOfferedThisSession = true;
        offerEnroll = await isPlatformAuthenticatorAvailable();
      }
      if (offerEnroll) {
        setShowEnrollPrompt(true);
        onEnrollPromptActiveChange?.(true);
      }
      onSignIn(token, refreshToken);

      // Task 11 fix round 2 (review IMPORTANT-3): a RETURNING customer who
      // already has a passkey should not be re-offered enrollment -- accepting
      // would create a SECOND, equally-unlabeled credential the customer
      // cannot tell apart from the first in the Security sub-section (Profile
      // section, SecurityPanel.tsx). listPasskeys()
      // is the existing Task 8 read contract (no new endpoint, no new client
      // method) -- it can only be called here, AFTER onSignIn above, because
      // that call is what makes the JWT live for embedApiFetch's ambient
      // token provider (auth-context's tokenRef updates SYNCHRONOUSLY inside
      // setToken -- rule §90 -- so this request already carries a valid
      // Bearer by the time it goes out, well before React's own re-render).
      // Best-effort: on failure, leave the offer showing rather than hide it
      // -- this is UX-only (ruling R2), so the worst case is an occasional
      // redundant offer, the same tolerance the "first sign-in this session"
      // approximation above already accepts.
      if (offerEnroll) {
        try {
          const existing = await listPasskeys();
          if (existing.length > 0) {
            setShowEnrollPrompt(false);
            onEnrollPromptActiveChange?.(false);
          }
        } catch {
          // Leave the offer showing -- see comment above.
        }
      }
    },
    [onSignIn, onEnrollPromptActiveChange, passkeyEligible],
  );

  const handleEnrollDone = useCallback(() => {
    setShowEnrollPrompt(false);
    onEnrollPromptActiveChange?.(false);
  }, [onEnrollPromptActiveChange]);

  // Auto-focus the code input when advancing to the code step (a11y).
  useEffect(() => {
    if (step === "code") {
      codeInputRef.current?.focus();
    }
  }, [step]);

  // -- TD-426: conditional (autofill) passkey sign-in ------------------------
  //
  // The browser offers a discoverable passkey inline in the email field's
  // autofill list. There is no prompt and no button; the request simply waits.
  //
  // Three properties this MUST hold, each learned from an adjacent incident:
  //
  //  * It NEVER blocks render or the OTP path. `signInWithPasskeyConditional`
  //    stays pending until the customer picks a passkey — that is the API
  //    working correctly — so it is started in an effect and never awaited by
  //    anything the customer is waiting on (TD-439's non-settling-probe trap,
  //    one layer out).
  //  * It aborts the moment the customer commits to OTP, and on unmount. A
  //    conditional request outliving its panel keeps an authenticator armed
  //    against a form nobody is looking at.
  //  * A cancelled or aborted ceremony is SILENT — `signInWithPasskeyConditional`
  //    resolves null rather than throwing, so ignoring an autofill suggestion
  //    can never paint an error. Only a genuine failure surfaces, and even then
  //    it must not imply OTP is unavailable (operator ruling R2).
  //
  // The autocomplete token is only attached once the browser has CONFIRMED
  // conditional support. Emitting `webauthn` in the token on a browser without
  // it is not inert everywhere — some engines drop the whole attribute, taking
  // ordinary email autofill down with it, which would be a regression for every
  // customer in exchange for a feature none of them can use.
  const [conditionalActive, setConditionalActive] = useState(false);
  const conditionalAbortRef = useRef<AbortController | null>(null);

  const abortConditionalPasskey = useCallback(() => {
    conditionalAbortRef.current?.abort();
    conditionalAbortRef.current = null;
  }, []);

  // -- Passkey sign-in (discoverable/usernameless) ---------------------------
  //
  // Task 8's signInWithPasskey() sends no email up front — discoverable login
  // (anti-enumeration: nothing here can reveal whether an account or a
  // passkey exists for a given address).
  //
  // handlePasskeySignIn lives BELOW abortConditionalPasskey (TD-453) so its
  // useCallback deps can name it without a use-before-declaration — the same
  // ordering discipline the OTP path (its other caller) already follows.
  //
  // Escape / dismissed the OS prompt) surfaces as a thrown error whose
  // `.name` is the literal string "NotAllowedError" — that is a NORMAL
  // outcome, not a failure: fall back to the OTP form silently, no
  // banner/toast/red text. Deliberately branching on `.name` rather than
  // importing and `instanceof`-checking the throwing class: two
  // similarly-named error classes exist under src/ one letter apart
  // (portal's PasskeyCanceledError vs. the marketplace embed's
  // MarketplacePasskeyCancelledError, embed/marketplace/api/passkeys.ts), and
  // an editor auto-import could silently bind the wrong one — `.name` is the
  // stable, rename-proof contract regardless of which class is in scope. Do
  // NOT switch this to `instanceof MarketplacePasskeyCancelledError`: that
  // reintroduces the exact auto-import footgun this comment exists to warn
  // against. Only a genuine failure (network error, server rejection, unsupported
  // browser) surfaces its real message via ceremonyMessageOf (fix-round-2
  // I-3) instead of the generic "Please try again" — a SecurityError from an
  // RP-ID mismatch, for one, is PERMANENT, and generic retry copy invites
  // the one action guaranteed not to work. The OTP form stays fully usable
  // either way — passkey is an additional convenience path, never a
  // replacement (operator ruling R2), and this function never implies OTP
  // is unavailable regardless of which message renders.
  const handlePasskeySignIn = useCallback(async () => {
    // TD-453: abort TD-426's pending conditional (autofill) request BEFORE
    // starting the modal ceremony. The customer committing to the explicit
    // button means the autofill offer is dead; leaving its request armed
    // against the same authenticator is at best waste and at worst a browser
    // that refuses the modal get() while another request is outstanding
    // (Chromium measured clean; Firefox has an open bug — the row's escalation
    // did not reproduce, this is defense the OTP path already had: it aborts
    // at its own entry below).
    abortConditionalPasskey();
    setPasskeyLoading(true);
    setError("");
    try {
      const result = await signInWithPasskey();
      // Match the OTP paths' `if (result?.token)` guard (handleVerifyOtp /
      // handleRecaptchaStepUp) rather than trusting the resolved value
      // unconditionally — same defensive posture against a malformed/empty
      // response, applied consistently across every sign-in path.
      if (result?.token) {
        onSignIn(result.token, result.refresh_token);
      }
    } catch (err) {
      if (!(err instanceof Error && err.name === "NotAllowedError")) {
        setError(ceremonyMessageOf(err, "Something went wrong. Please try again."));
      }
    } finally {
      setPasskeyLoading(false);
    }
  }, [onSignIn, abortConditionalPasskey]);

  useEffect(() => {
    if (!showPasskeySignIn) return;
    let cancelled = false;
    const controller = new AbortController();
    conditionalAbortRef.current = controller;

    void (async () => {
      if (!(await isConditionalMediationAvailable())) return;
      // The panel can have unmounted, or the customer moved to the code step,
      // while the capability probe was resolving.
      if (cancelled || controller.signal.aborted) return;
      setConditionalActive(true);
      try {
        const result = await signInWithPasskeyConditional(controller.signal);
        if (cancelled || !result?.token) return;
        onSignIn(result.token, result.refresh_token);
      } catch {
        // Deliberately silent. A background request the customer never asked
        // for must not produce a banner on a form they are still typing into;
        // the explicit "Continue with passkey" button remains, and reports its
        // own failures through ceremonyMessageOf.
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      conditionalAbortRef.current = null;
    };
  }, [showPasskeySignIn, onSignIn]);

  // -- Step 1: request OTP ---------------------------------------------------

  const handleRequestOtp = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      const trimmed = email.trim();
      if (!trimmed) {
        setError("Please enter your email address.");
        // NOT aborted here, deliberately: an empty-field slip is not a decision
        // to use OTP. Killing the conditional request on a validation bounce
        // would silently remove the autofill option for the rest of the
        // session, and it can only be restarted by remounting the panel.
        return;
      }
      // The customer has committed to email-OTP with a real address. Withdraw
      // the background conditional ceremony now (TD-426) — from here the flow
      // moves to the code step, where an armed authenticator prompt belongs to
      // a form that is no longer on screen.
      abortConditionalPasskey();
      setLoading(true);
      setError("");

      try {
        // Lazy reCAPTCHA (rule #20): inject script + execute only on submit.
        const recaptchaToken = recaptchaSiteKey
          ? await executeRecaptchaToken(recaptchaSiteKey, "marketplace_request_otp")
          : "";

        const result = await embedApiFetch<{ message: string; expires_in: number }>(
          "/api/v1/marketplace/auth/request-otp",
          { method: "POST", body: { email: trimmed, recaptcha_token: recaptchaToken } },
        );
        setInfoMessage(result?.message ?? "A verification code has been sent.");
        setCode("");
        setStep("code");
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 403) {
            setError("Verification failed, please try again.");
          } else if (err.status === 400) {
            setError("Please enter a valid email address.");
          } else {
            setError(err.message || "Something went wrong. Please try again.");
          }
        } else {
          setError("Something went wrong. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [email, recaptchaSiteKey],
  );

  // -- Step 2: verify OTP ----------------------------------------------------

  /** Format a non-step-up verify error into a user-facing message. */
  const formatVerifyError = useCallback((err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 401) return "Invalid or expired code. Please try again.";
      if (err.status === 400) return "Enter the 6-digit code sent to your email.";
      return err.message || "Something went wrong. Please try again.";
    }
    return "Something went wrong. Please try again.";
  }, []);

  /** Step-up: backend signalled RECAPTCHA_REQUIRED after K failed OTP
   *  attempts. Acquire a token transparently and resubmit the same code. */
  const handleRecaptchaStepUp = useCallback(
    async (trimmedEmail: string, trimmedCode: string) => {
      setStepUp(true);
      try {
        const token = recaptchaSiteKey
          ? await executeRecaptchaToken(recaptchaSiteKey, "marketplace_verify_otp")
          : "";
        const result = await embedApiFetch<{ token: string; refresh_token?: string }>(
          "/api/v1/marketplace/auth/verify-otp",
          { method: "POST", body: { email: trimmedEmail, code: trimmedCode, recaptcha_token: token } },
        );
        if (result?.token) {
          // TD-366: refresh_token is absent on a pre-TD-366 backend (legacy
          // bare-token shape) — optional, matching setToken's signature.
          await finishOtpSignIn(result.token, result.refresh_token);
        }
      } catch (err) {
        setError(formatVerifyError(err));
      } finally {
        setStepUp(false);
        setLoading(false);
      }
    },
    [recaptchaSiteKey, finishOtpSignIn, formatVerifyError],
  );

  const handleVerifyOtp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedCode = code.trim();
      if (!/^\d{6}$/.test(trimmedCode)) {
        setError("Enter the 6-digit code sent to your email.");
        return;
      }
      setLoading(true);
      setError("");
      setStepUp(false);

      try {
        const result = await embedApiFetch<{ token: string; refresh_token?: string }>(
          "/api/v1/marketplace/auth/verify-otp",
          { method: "POST", body: { email: email.trim(), code: trimmedCode } },
        );
        if (result?.token) {
          await finishOtpSignIn(result.token, result.refresh_token);
        }
      } catch (err) {
        // 403 + RECAPTCHA_REQUIRED: step-up — acquire token and resubmit.
        if (err instanceof ApiError && err.status === 403 && err.code === "RECAPTCHA_REQUIRED") {
          await handleRecaptchaStepUp(email.trim(), trimmedCode);
          return;
        }
        setError(formatVerifyError(err));
        setLoading(false);
      }
    },
    [email, code, finishOtpSignIn, handleRecaptchaStepUp, formatVerifyError],
  );

  // -- Back to email step ----------------------------------------------------

  const handleChangeEmail = useCallback(() => {
    setStep("email");
    setCode("");
    setError("");
    setInfoMessage("");
  }, []);

  // -- Resend code -----------------------------------------------------------

  const handleResendCode = useCallback(async () => {
    setError("");
    setInfoMessage("");
    await handleRequestOtp();
  }, [handleRequestOtp]);

  // Clear error when user changes input.
  const onEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError("");
  }, [error]);

  const onCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only digits, max 6 chars.
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(val);
    if (error) setError("");
  }, [error]);

  // -- Shared styles ---------------------------------------------------------

  // inputStyle is still consumed by the raw code-input above (kept raw for
  // the codeInputRef autofocus — see the comment on that input). buttonStyle
  // was removed when the two submit buttons converted to EmbedButton, which
  // owns its own loading-aware primary styling.
  const inputStyle = {
    width: "100%",
    padding: t.inputPadding,
    fontSize: t.inputFontSize,
    fontFamily: t.fontFamily,
    border: `1px solid ${t.colors.border}`,
    borderRadius: t.borderRadius,
    boxSizing: "border-box" as const,
    marginBottom: t.spacing.md,
  };

  // Task 11: the post-signup passkey-enrollment offer REPLACES the normal
  // form entirely (never layered alongside it) — the customer is already
  // signed in at this point (finishOtpSignIn calls onSignIn first), so there
  // is nothing left for the OTP form to do.
  if (showEnrollPrompt) {
    return (
      <div style={{ maxWidth: 400, margin: "0 auto", padding: t.spacing.xl }}>
        <PasskeyEnrollPrompt onDone={handleEnrollDone} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 400, margin: "0 auto", padding: t.spacing.xl }}>
      <h2
        style={{
          fontSize: t.fontSize.xl,
          fontWeight: 700,
          color: t.colors.text,
          margin: `0 0 ${t.spacing.md}px 0`,
          textAlign: "center",
          // Item (r): the heading is now a sentence rather than three words,
          // so it wraps to two lines in the 400px-max panel. The browser
          // default leading (~1.2) on 20px+ type reads as two crowded lines
          // under a centred layout; 1.3 keeps the pair legible without
          // pushing the email field down a whole step.
          lineHeight: 1.3,
        }}
      >
        {SIGN_IN_HEADING}
      </h2>

      {/* The standalone hint ("Enter your email and we'll send you a
          verification code.") was removed deliberately: the submit button
          already says "Send verification code", so the sentence restated the
          control directly beneath it. Three stacked explanations — heading,
          divider, hint — were what made this panel read as busy, and each line
          also pushed the panel taller, which is the same growth that runs it
          under the page footer. Fewer words is both the UX fix and part of the
          layout fix. */}

      {step === "email" && (
        <form onSubmit={handleRequestOtp} noValidate>
          <label
            htmlFor="embed-signin-email"
            style={{ fontSize: t.fontSize.sm, fontWeight: 500, color: t.colors.text, display: "block", marginBottom: t.spacing.xs }}
          >
            Email
          </label>
          <div style={{ marginBottom: t.spacing.md }}>
            <EmbedInput
              id="embed-signin-email"
              type="email"
              // TD-426: the `webauthn` token is what tells the browser to offer
              // a discoverable passkey inside THIS field's autofill list. It is
              // added only after isConditionalMediationAvailable() confirms
              // support — see the effect above for why an unconditional token
              // is not a safe default.
              autoComplete={conditionalActive ? "username webauthn" : "email"}
              value={email}
              onChange={onEmailChange}
              disabled={loading || passkeyLoading}
            />
          </div>
          {error && (
            <p role="alert" aria-live="assertive" style={{ color: t.colors.error, fontSize: t.fontSize.xs, margin: `0 0 ${t.spacing.sm}px 0` }}>
              {error}
            </p>
          )}
          <EmbedButton type="submit" variant="primary" fullWidth loading={loading} disabled={passkeyLoading}>
            Send verification code
          </EmbedButton>
        </form>
      )}

      {/* Task 15 (account-area redesign, F1): the passkey action moved BELOW
          the email form (was: before it, above the email field) so the primary
          path reads first — OTP is never gated on this feature (operator
          ruling R2) — with the alternative under an "or" divider. The
          divider's visible word is aria-hidden: the separator ROLE carries the
          semantics, so a screen reader announces a separator between two
          labelled actions rather than the word itself. jsdom pins the DOM
          order; the visual order is proven in a real browser
          (smoke-embed-ds-tokens "sign-in overlay (passkey eligible)"). The
          button stays OUTSIDE the <form> (type="button" regardless — rule
          §73) so no browser quirk can ever make the alternative action submit
          the OTP form. */}
      {step === "email" && showPasskeySignIn && (
        <div style={{ marginTop: t.spacing.md }}>
          <div
            role="separator"
            style={{
              display: "flex",
              alignItems: "center",
              gap: t.spacing.sm,
              margin: `0 0 ${t.spacing.md}px 0`,
              color: t.colors.textSecondary,
              fontSize: t.fontSize.xs,
            }}
          >
            <span style={{ flex: 1, borderTop: `1px solid ${t.colors.borderSubtle}` }} />
            <span aria-hidden="true">or</span>
            <span style={{ flex: 1, borderTop: `1px solid ${t.colors.borderSubtle}` }} />
          </div>
          <EmbedButton
            type="button"
            variant="secondary"
            fullWidth
            loading={passkeyLoading}
            disabled={loading}
            onClick={handlePasskeySignIn}
          >
            Continue with passkey
          </EmbedButton>
        </div>
      )}

      {step === "code" && (
        <form onSubmit={handleVerifyOtp} noValidate>
          {infoMessage && (
            <p aria-live="polite" style={{ color: t.colors.textSecondary, fontSize: t.fontSize.sm, margin: `0 0 ${t.spacing.md}px 0`, textAlign: "center" }}>
              {infoMessage}
            </p>
          )}
          <label
            htmlFor="embed-signin-code"
            style={{ fontSize: t.fontSize.sm, fontWeight: 500, color: t.colors.text, display: "block", marginBottom: t.spacing.xs }}
          >
            Verification code
          </label>
          {/* raw <input>: code input kept raw (primitive sweep TD-NEW).
              EmbedInput is a function component without forwardRef, and this
              input is the target of codeInputRef.current?.focus() in the
              step transition effect above — an a11y autofocus that silently
              no-ops if the ref doesn't reach the underlying <input>. On
              React 18.3 a ref passed to a function component warns and is
              dropped, so EmbedInput cannot host this control until it grows
              forwardRef support. The custom letterSpacing/textAlign are
              secondary; the ref is load-bearing. */}
          <input
            ref={codeInputRef}
            id="embed-signin-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={onCodeChange}
            disabled={loading}
            placeholder="000000"
            maxLength={6}
            style={{ ...inputStyle, letterSpacing: "0.2em", textAlign: "center" as const }}
          />
          {stepUp && (
            <p role="status" aria-live="polite" style={{ color: t.colors.textSecondary, fontSize: t.fontSize.xs, margin: `0 0 ${t.spacing.sm}px 0`, textAlign: "center" }}>
              Running an extra security check. One moment...
            </p>
          )}
          {error && (
            <p role="alert" aria-live="assertive" style={{ color: t.colors.error, fontSize: t.fontSize.xs, margin: `0 0 ${t.spacing.sm}px 0` }}>
              {error}
            </p>
          )}
          <EmbedButton type="submit" variant="primary" fullWidth loading={loading}>
            Sign in
          </EmbedButton>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: t.spacing.md }}>
            <EmbedButton type="button" variant="ghost" size="sm" onClick={handleChangeEmail} disabled={loading}>
              Change email
            </EmbedButton>
            <EmbedButton type="button" variant="ghost" size="sm" onClick={handleResendCode} disabled={loading}>
              Resend code
            </EmbedButton>
          </div>
        </form>
      )}
    </div>
  );
}
